import { BarChart } from '@tremor/react';
import React, { useMemo } from 'react';

import { useAdminAiUsageSummary } from '@dashboard/hooks/queries/useAdminQueries';
import useUrlState from '@dashboard/hooks/useUrlState';

import { Skeleton } from '@dashboard/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@dashboard/components/ui/tabs';

import AiUsagePeriodSelect from './AiUsagePeriodSelect';
import AiUsageStoresTable from './AiUsageStoresTable';
import AiUsageTurnsTable from './AiUsageTurnsTable';
import { type DailyUsage, fillEmptyDays, formatMicros } from './aiUsagePeriod';
import useAiUsagePeriod from './useAiUsagePeriod';

type SurfaceTotals = { costMicros: number; turns: number };

type AiUsageSummary = {
  costMicros: number;
  turns: number;
  offTopicTurns: number;
  stores: number;
  bySurface: { create: SurfaceTotals; edit: SurfaceTotals; ask: SurfaceTotals };
  storesAtBudget: number;
  usersFlagged24h: number;
  daily: DailyUsage[];
};

const STATS: Array<{ label: string; value: (summary: AiUsageSummary) => string }> = [
  { label: 'Estimated spend', value: summary => formatMicros(summary.costMicros) },
  { label: 'Turns', value: summary => summary.turns.toLocaleString() },
  { label: 'Create', value: summary => formatMicros(summary.bySurface.create.costMicros) },
  { label: 'Edit', value: summary => formatMicros(summary.bySurface.edit.costMicros) },
  { label: 'Ask', value: summary => formatMicros(summary.bySurface.ask.costMicros) },
  { label: 'Off-topic flags', value: summary => summary.offTopicTurns.toLocaleString() },
  { label: 'Stores using AI', value: summary => summary.stores.toLocaleString() },
  { label: 'Stores at budget', value: summary => summary.storesAtBudget.toLocaleString() },
  { label: 'Users flagged (24h)', value: summary => summary.usersFlagged24h.toLocaleString() },
];

const AiUsage = () => {
  const [query, setQuery] = useUrlState() as [
    Record<string, unknown>,
    (updates: Record<string, unknown>) => void,
  ];

  const { period, range, setPeriod } = useAiUsagePeriod();
  const tab = query.tab === 'turns' ? 'turns' : 'stores';

  const { data: summary, isLoading } = useAdminAiUsageSummary(range) as {
    data?: AiUsageSummary;
    isLoading: boolean;
  };

  const chartData = useMemo(
    () =>
      summary
        ? fillEmptyDays(summary.daily, range).map(({ date, create, edit, ask }) => ({
            date,
            Create: create,
            Edit: edit,
            Ask: ask,
          }))
        : [],
    [summary, range],
  );

  return (
    <div className="pr-4 space-y-6">
      <div className="flex items-end justify-between">
        <h1>AI usage</h1>
        <AiUsagePeriodSelect value={period} onValueChange={setPeriod} />
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STATS.map(({ label, value }) => (
          <div key={label} className="rounded-lg border border-gray-200 dark:border-border p-4">
            <dt className="text-sm text-gray-500 dark:text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-xl text-gray-900 dark:text-foreground">
              {summary ? value(summary) : isLoading ? <Skeleton className="h-7 w-16" /> : '-'}
            </dd>
          </div>
        ))}
      </dl>

      <div className="rounded-lg border border-gray-200 dark:border-border p-4">
        <BarChart
          data={chartData}
          index="date"
          categories={['Create', 'Edit', 'Ask']}
          stack
          valueFormatter={formatMicros}
          colors={['indigo', 'emerald', 'amber']}
          className="h-56"
        />
      </div>

      <Tabs value={tab} onValueChange={value => setQuery({ tab: value })}>
        <TabsList>
          <TabsTrigger value="stores">Stores</TabsTrigger>
          <TabsTrigger value="turns">Turns</TabsTrigger>
        </TabsList>
        <TabsContent value="stores">
          <AiUsageStoresTable filters={range} period={period} />
        </TabsContent>
        <TabsContent value="turns">
          <AiUsageTurnsTable filters={range} period={period} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AiUsage;
